import type { ChatOrchestrationWebSocketPayload } from "./chatService";

type Translate = (key: string, options?: Record<string, unknown>) => string;

type ChatErrorPayloadView = {
  error?: ChatOrchestrationWebSocketPayload["error"];
  message?: ChatOrchestrationWebSocketPayload["message"];
  type?: ChatOrchestrationWebSocketPayload["type"];
  result?: ChatOrchestrationWebSocketPayload["result"];
  period?: ChatOrchestrationWebSocketPayload["period"];
  resetDate?: ChatOrchestrationWebSocketPayload["resetDate"];
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function looksLikeErrorCode(value: string): boolean {
  return /^[A-Z][A-Z0-9_]{2,}$/.test(value.trim());
}

/**
 * Maps backend orchestration error payloads to a stable, localized client message.
 * Machine codes and technical details are never shown as the primary user text.
 */
export function resolveChatClientErrorMessage(
  payload: ChatErrorPayloadView,
  t: Translate
): string {
  const codeRaw = typeof payload.error === "string" ? payload.error.trim() : "";
  const result = asRecord(payload.result);
  const resultCode =
    typeof result?.code === "string" && result.code.trim() ? result.code.trim() : "";
  const code = codeRaw || resultCode;

  if (code === "QUOTA_EXCEEDED") {
    return t("chat.quotaExhausted");
  }
  if (code === "PACKAGE_QUERY_DETAIL_UNAVAILABLE") {
    const packageLabel =
      (typeof result?.packageLabel === "string" && result.packageLabel) ||
      (typeof result?.packageType === "string" && result.packageType) ||
      "";
    return t("chat.packageQueryDetailUnavailable", { package: packageLabel });
  }
  if (code === "NO_ACCESSIBLE_RESOURCES") {
    return t("chat.noAccessibleResources");
  }
  if (code === "TIMEOUT") {
    return t("chat.responseTimeout");
  }
  if (code === "CONFIG_ERROR") {
    return t("chat.configError");
  }
  if (
    code === "CONTRACT_VIOLATION" ||
    code === "ORCHESTRATION_ERROR" ||
    looksLikeErrorCode(code)
  ) {
    return t("chat.genericError");
  }

  const message = typeof payload.message === "string" ? payload.message.trim() : "";
  if (message && !looksLikeErrorCode(message)) {
    // Prefer standard copy for long/technical provider text.
    if (message.length > 180 || /exception|stack|at com\.|java\./i.test(message)) {
      return t("chat.genericError");
    }
    return message;
  }

  return t("chat.genericError");
}

export function isOrchestrationErrorPayload(
  payload: Pick<ChatErrorPayloadView, "error" | "type">
): boolean {
  if (typeof payload.error === "string" && payload.error.trim()) {
    return true;
  }
  return String(payload.type || "").toLowerCase() === "error";
}
